import { ElementRef, useEffect, useRef, useState } from 'react';

import {
  Card,
  UIElement,
  SubmitData,
  SubmitActions,
  PaymentAction,
  UIElementProps,
  PaymentFailedData,
  AdyenCheckoutError,
  AdditionalDetailsData,
  AdditionalDetailsActions,
} from '@adyen/adyen-web';
import { useTranslation } from 'react-i18next';
import { useErrorBoundary } from 'react-error-boundary';

import {
  ESdkEvents,
  EventParams,
  AdyenFinalizePaymentResponse,
  GetAdyenPaymentStatusResponse,
  AppchargeErrorType,
} from '../../../../../contracts';
import { sendFEEvent } from '../../../../../eventUtil';
import { useAPI } from '../../../../../services/api.service';
import { useAdyenCheckout } from '../hooks/use-adyen-checkout';
import { useCheckout } from '../../../../../hooks/use-checkout';
import { usePaymentState } from '../../../hooks/use-payment-state';
import { useOrderPolling } from '../../../hooks/use-order-polling';
import { ADYEN_CARD_CONFIGURATION, createAdyenCardFinalizeRequestBody } from '../utils';
import { generateAppchargeErrorMessage } from '../../../../../utils/generate-appcharge-error-message';
import { apmLatencyTracker } from '../../../../../utils/apm-latency-tracker';

interface HandlePayParams {
  email: string;
  cardholderName?: string;
  shouldSaveCard: boolean;
  isUserDetailsValid: boolean;
}

export interface AdyenCardDetails {
  bin: string;
  endDigits: string;
  expiryDate: string;
}

const CARD_FIELD = 'encryptedCardNumber';
const DATE_FIELD = 'encryptedExpiryDate';

export function useAdyenCard() {
  const ref = useRef<ElementRef<'div'>>();
  const isAdyenPaymentInProgress = useRef(false);
  const submitParamsRef = useRef<any>();
  const cardActionsRef = useRef<SubmitActions>();
  const cardDetailsRef = useRef<AdyenCardDetails>({
    bin: '',
    endDigits: '',
    expiryDate: '',
  });
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const [cardComponentSDK, setCardComponentSDK] = useState<Card>();
  const [threeDSComponent, setThreeDSComponent] = useState<UIElement | null>(null);
  const { selectedPaymentMethod, checkoutData, getEventParams } = useCheckout();
  const setIsApmLoading = usePaymentState((state) => state.setIsApmLoading);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setIsPaymentElementLoading = usePaymentState((state) => state.setIsPaymentElementLoading);
  const { checkoutInstance } = useAdyenCheckout(selectedPaymentMethod);
  const { startOrderPolling } = useOrderPolling();
  const { API } = useAPI();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { showBoundary } = useErrorBoundary();

  const handleError = (appChargeErrorType?: AppchargeErrorType) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    setErrorMessage(errorMessage);
    isAdyenPaymentInProgress.current = false;
  };

  const onSubmit = async (
    state: SubmitData,
    component: UIElement<UIElementProps>,
    actions: SubmitActions
  ) => {
    cardActionsRef.current = actions;
    const finalizePaymentRequestBody = createAdyenCardFinalizeRequestBody(
      state.data.paymentMethod,
      state.data.browserInfo,
      selectedPaymentMethod,
      submitParamsRef.current,
      cardDetailsRef.current
    );
    try {
      const res = await API.finalizePayment<AdyenFinalizePaymentResponse>(
        finalizePaymentRequestBody
      );
      handleServerResponse(res, actions);
    } catch (error: any) {
      const errResponse = error?.response?.data;
      showBoundary(t('somethingWentWrong'));
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: errResponse?.message,
        isNewPaymentMethodSaved: submitParamsRef.current?.shouldSaveCard,
        error: {
          isFatal: true,
          reason: errResponse?.message,
        },
      });
      actions.reject();
    }
  };

  const handleServerResponse = (
    finalizePaymentResponse: AdyenFinalizePaymentResponse,
    actions: SubmitActions
  ) => {
    const { status, resultCode, action } = finalizePaymentResponse.adyen;
    if (status === 'succeeded') {
      actions.resolve({ resultCode: resultCode, action });
    } else if (status === 'failed') {
      actions.resolve({ resultCode: resultCode });
      handleError(finalizePaymentResponse.appChargeErrorType);
    } else if (status === 'redirect') {
      if (action) {
        handle3DSFlow(action);
      } else {
        actions.resolve({ resultCode: resultCode });
      }
    }
  };

  const handle3DSFlow = (action: PaymentAction) => {
    const actionComponent = checkoutInstance!.createFromAction(action, {
      ...ADYEN_CARD_CONFIGURATION,
      onAdditionalDetails,
      onError,
      onPaymentCompleted,
      onPaymentFailed,
    });
    setThreeDSComponent(actionComponent);
  };

  const onPaymentCompleted = () => {
    const FEeventParams: Partial<EventParams> = {
      isNewPaymentMethodSaved: submitParamsRef.current?.shouldSaveCard,
    };
    startOrderPolling(checkoutData?.orderId!, FEeventParams);
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
      ...getEventParams(),
      ...FEeventParams,
    });
  };

  const onAdditionalDetails = async (
    state: AdditionalDetailsData,
    _: UIElement,
    actions: AdditionalDetailsActions
  ) => {
    const threeDSResult = state.data.details.threeDSResult;

    if (threeDSResult) {
      try {
        setThreeDSComponent(null);
        const paymentStatusResponse = await API.getPaymentStatus_NEW<GetAdyenPaymentStatusResponse>(
          threeDSResult,
          selectedPaymentMethod?.id as string
        );
        const resultCode = paymentStatusResponse.resultCode;
        if (resultCode) {
          cardActionsRef.current?.resolve({ resultCode });
        } else {
          cardActionsRef.current?.reject();
          handleError();
        }
      } catch (error) {
        setThreeDSComponent(null);
        handleError();
        cardActionsRef.current?.reject();
      }
    } else {
      setThreeDSComponent(null);
      cardActionsRef.current?.reject();
      handleError();
    }
  };

  const onPaymentFailed = async (_: PaymentFailedData, component?: UIElement<UIElementProps>) => {
    component?.setStatus('ready');
    cardComponentSDK?.setStatus('ready');
    setIsPaymentLoading(false);
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
      ...getEventParams(),
      isNewPaymentMethodSaved: submitParamsRef.current?.shouldSaveCard,
    });
    isAdyenPaymentInProgress.current = false;
    checkAndReInitializeCard();
  };

  const onError = (_: AdyenCheckoutError, component?: UIElement<UIElementProps>) => {
    component?.setStatus('ready');
    cardComponentSDK?.setStatus('ready');
    setIsPaymentLoading(false);
    sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
      ...getEventParams(),
      isNewPaymentMethodSaved: submitParamsRef.current?.shouldSaveCard,
    });
    handleError();
    checkAndReInitializeCard();
  };

  const checkAndReInitializeCard = () => {
    // check if the iframe is mounted, as it can unmount itself in case of certain errors
    if (checkoutInstance && !ref.current?.children.length) {
      const cardComponent = new Card(checkoutInstance, {
        ...ADYEN_CARD_CONFIGURATION,
        onLoad: () => {
          setIsPaymentElementLoading(false);
          setIsApmLoading(false);
        },
        onSubmit,
        onError,
        onFieldValid: ({ fieldType, valid, endDigits, expiryDate }) => {
          if (fieldType === CARD_FIELD && valid) {
            cardDetailsRef.current.endDigits = endDigits || '';
            cardComponent.setFocusOn(DATE_FIELD);
          } else if (fieldType === DATE_FIELD && valid) {
            cardDetailsRef.current.expiryDate = expiryDate || '';
          }
        },
        onBinValue: ({ binValue }) => {
          cardDetailsRef.current.bin = binValue || '';
        },
        onPaymentFailed,
        onPaymentCompleted,
        onAdditionalDetails,
      }).mount(ref.current || '');
      setCardComponentSDK(cardComponent);
    }
  };

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Adyen Card', selectedPaymentMethod?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }

    let cardComponent: Card;
    if (checkoutInstance) {
      cardComponent = new Card(checkoutInstance, {
        ...ADYEN_CARD_CONFIGURATION,
        onLoad: () => {
          setIsPaymentElementLoading(false);
          setIsApmLoading(false);

          try {
            apmLatencyTracker.endTracking('Adyen Card', selectedPaymentMethod?.id, {
              paymentMethodName: selectedPaymentMethod?.paymentMethodName,
              paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
              provider: 'Adyen',
            });
          } catch (error) {
            console.error('Failed to end APM latency tracking:', error);
          }
        },
        onSubmit,
        onError,

        onFieldValid: ({ fieldType, valid, endDigits, expiryDate }) => {
          if (fieldType === CARD_FIELD && valid) {
            cardDetailsRef.current.endDigits = endDigits || '';

            cardComponent.setFocusOn(DATE_FIELD);
          } else if (fieldType === DATE_FIELD && valid) {
            cardDetailsRef.current.expiryDate = expiryDate || '';
          }
        },
        onPaymentFailed,
        onPaymentCompleted,
        onAdditionalDetails,
        onBinValue: ({ binValue }) => {
          cardDetailsRef.current.bin = binValue || '';
        },
      }).mount(ref.current || '');
      setCardComponentSDK(cardComponent);
    }

    return () => {
      cardComponent?.unmount();
    };
  }, [checkoutInstance]);

  const handlePay = (submitParams: HandlePayParams) => {
    submitParamsRef.current = { ...submitParams };
    if (
      cardComponentSDK?.isValid &&
      submitParams.isUserDetailsValid &&
      !isAdyenPaymentInProgress.current
    ) {
      isAdyenPaymentInProgress.current = true;
      setIsPaymentLoading(true);
      cardComponentSDK?.submit();
    } else {
      cardComponentSDK?.showValidation();
    }
  };

  return {
    cardComponentRef: ref,
    handlePay,
    threeDSComponent,
  };
}
